import {ChangeDetectionStrategy, Component, computed, effect, inject, input, output} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {LicencePlanDetails} from '../../../../../api';
import {FeatureSelect} from '../../../../../api/models';
import {TextInput} from '../../../../components/text-input/text-input';
import {TextArea} from '../../../../components/text-area/text-area';
import {Label} from '../../../../components/label/label';
import {LicencePlanFormValue} from '../../models/licence-plan-form-value';
import {FeatureStore} from '../../services/feature-store';
import {Divider} from 'primeng/divider';
import {Checkbox} from 'primeng/checkbox';

@Component({
  selector: 'elcia-licence-plan-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TranslatePipe, TextInput, TextArea, Label, Divider, Checkbox],
  templateUrl: './licence-plan-form.html',
})
export class LicencePlanForm {
  private readonly featureStore = inject(FeatureStore);

  initialValue = input<LicencePlanDetails | undefined>();
  onSubmit = output<LicencePlanFormValue>();

  // Code en lecture seule : exposé pour l'affichage, jamais éditable (absent de UpdateLicencePlanRequest).
  protected readonly code = computed(() => this.initialValue()?.code ?? null);

  protected readonly featureOptions = computed<FeatureSelect[]>(() => this.featureStore.features());

  protected form = new FormGroup({
    nameFr: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
    nameEn: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
    nameNl: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
    descriptionFr: new FormControl<string | null>(null),
    descriptionEn: new FormControl<string | null>(null),
    descriptionNl: new FormControl<string | null>(null),
    includedFeatureCodes: new FormControl<number[]>([], {nonNullable: true}),
  });

  onInitialValueChange = effect(() => {
    const val = this.initialValue();
    if (!val) {
      this.form.reset();
      return;
    }

    this.form.patchValue({
      nameFr: val.nameFr,
      nameEn: val.nameEn,
      nameNl: val.nameNl,
      descriptionFr: val.descriptionFr ?? null,
      descriptionEn: val.descriptionEn ?? null,
      descriptionNl: val.descriptionNl ?? null,
      includedFeatureCodes: val.includedFeatureCodes ?? [],
    });
  });

  public submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const formValue = this.form.getRawValue();
    this.onSubmit.emit({
      id: this.initialValue()?.id ?? null,
      nameFr: formValue.nameFr,
      nameEn: formValue.nameEn,
      nameNl: formValue.nameNl,
      descriptionFr: formValue.descriptionFr,
      descriptionEn: formValue.descriptionEn,
      descriptionNl: formValue.descriptionNl,
      includedFeatureCodes: formValue.includedFeatureCodes,
    });
  }
}
